import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'

import { isApiError } from 'api/apiHelpers'
import { CountryPicker } from 'features/identityCheck/components/countryPicker/CountryPicker'
import { useSubscriptionContext } from 'features/identityCheck/context/SubscriptionContextProvider'
import { useNavigateForwardToStepper } from 'features/identityCheck/helpers/useNavigateForwardToStepper'
import { invalidateStepperInfoQueries } from 'features/identityCheck/pages/helpers/invalidateStepperQueries'
import { useSaveStep } from 'features/identityCheck/pages/helpers/useSaveStep'
import { findCountry } from 'features/identityCheck/pages/phoneValidation/helpers/findCountry'
import { formatPhoneNumberWithPrefix } from 'features/identityCheck/pages/phoneValidation/helpers/formatPhoneNumber'
import {
  PhoneNumberFormValues,
  phoneNumberSchema,
} from 'features/identityCheck/pages/phoneValidation/helpers/phoneNumberSchema'
import { IdentityCheckStep } from 'features/identityCheck/types'
import { usePatchProfileMutation } from 'queries/profile/usePatchProfileMutation'
import { METROPOLITAN_FRANCE } from 'shared/countries/constants'
import { Form } from 'ui/components/Form'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Banner } from 'ui/designSystem/Banner/Banner'
import { Button } from 'ui/designSystem/Button/Button'
import { TextInput } from 'ui/designSystem/TextInput/TextInput'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { Typo } from 'ui/theme'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

export const SetPhoneNumberWithoutValidation = () => {
  const { dispatch, phoneValidation } = useSubscriptionContext()
  const { control, handleSubmit, getValues, setError, formState } = useForm<PhoneNumberFormValues>({
    resolver: yupResolver(phoneNumberSchema),
    defaultValues: {
      phoneNumber: phoneValidation?.phoneNumber,
      countryId: phoneValidation?.country.countryCode ?? METROPOLITAN_FRANCE.id,
    },
    mode: 'onChange',
  })

  const { navigateForwardToStepper } = useNavigateForwardToStepper()
  const saveStep = useSaveStep()
  const { mutate: patchProfile } = usePatchProfileMutation({
    onSuccess: () => {
      const { phoneNumber, countryId } = getValues()
      const country = findCountry(countryId)
      if (!country) return

      dispatch({
        type: 'SET_PHONE_NUMBER',
        payload: {
          phoneNumber,
          country: {
            countryCode: country.id,
            callingCode: country.callingCode,
          },
        },
      })
      saveStep(IdentityCheckStep.PHONE_VALIDATION)
      invalidateStepperInfoQueries()
      navigateForwardToStepper()
    },
    onError: (error) => {
      isApiError(error) && setError('phoneNumber', { message: error.message })
    },
  })

  const onSubmit = async ({ phoneNumber, countryId }: PhoneNumberFormValues) => {
    const country = findCountry(countryId)
    if (!country) {
      return
    }
    const phoneNumberWithPrefix = formatPhoneNumberWithPrefix(phoneNumber, country.callingCode)
    patchProfile({ phoneNumber: phoneNumberWithPrefix })
  }

  const submit = handleSubmit(onSubmit)

  return (
    <PageWithHeader
      shouldLimitWidth
      title="Numéro de téléphone"
      scrollChildren={
        <ViewGap gap={8}>
          <ViewGap gap={4}>
            <Typo.Title3 {...setTextSemantic('h2')}>Saisis ton numéro de téléphone</Typo.Title3>
            <Banner label="Ton numéro pourra être utilisé pour recevoir des infos sur tes futures réservations." />
          </ViewGap>
          <Form.MaxWidth>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field, fieldState }) => (
                <ViewGap gap={2}>
                  <TextInput
                    autoComplete="tel"
                    autoCapitalize="none"
                    keyboardType="number-pad"
                    label="Numéro de téléphone"
                    description="0639980123"
                    value={field.value}
                    onChangeText={field.onChange}
                    onSubmitEditing={submit}
                    accessibilityHint={fieldState.error?.message}
                    leftComponent={
                      <Controller
                        name="countryId"
                        control={control}
                        render={({ field }) => {
                          const selectedCountry = findCountry(field.value) ?? METROPOLITAN_FRANCE

                          return (
                            <CountryPicker
                              selectedCountry={selectedCountry}
                              onSelect={(country) => field.onChange(country.id)}
                            />
                          )
                        }}
                      />
                    }
                    testID="Entrée pour le numéro de téléphone"
                    errorMessage={fieldState.error?.message}
                  />
                </ViewGap>
              )}
            />
          </Form.MaxWidth>
        </ViewGap>
      }
      fixedBottomChildren={
        <Button
          type="submit"
          disabled={!formState.isValid}
          wording="Continuer"
          accessibilityLabel="Continuer vers l’étape suivante"
          onPress={submit}
        />
      }
    />
  )
}
